/**
 * Translate an axios/API error into a clean, user-facing message.
 * Never surfaces raw exception strings or technical details to the UI.
 *
 * @param {any} err - the caught error (axios error, Error, etc.)
 * @param {string} fallback - message to use when nothing specific is known
 * @returns {string}
 */
export function getFriendlyError(err, fallback = 'Something went wrong. Please try again.') {
  // Network / no-response errors (api.js sets err.userMessage for these)
  if (err?.userMessage) return err.userMessage;
  if (err?.code === 'ERR_NETWORK' || err?.code === 'ECONNABORTED' || (err && !err.response)) {
    return "We're having trouble reaching the server right now. Please try again in a moment.";
  }

  const status = err?.response?.status;
  const data = err?.response?.data;

  // Prefer a human-readable message the server explicitly provided.
  const serverMessage =
    (typeof data === 'string' && data) ||
    data?.error ||
    data?.message ||
    null;

  // Map common statuses to friendly copy.
  if (status === 401) return serverMessage || 'Incorrect email or password.';
  if (status === 403) return serverMessage || 'You do not have permission to do that.';
  if (status === 404) return serverMessage || fallback;
  if (status === 409) return serverMessage || 'That already exists. Try signing in instead.';
  if (status === 429) return 'Too many attempts. Please wait a moment and try again.';
  if (status >= 500) return 'The server ran into a problem. Please try again shortly.';

  // Only return a server message if it looks like human copy (not a stack/JSON blob).
  if (serverMessage && serverMessage.length < 200 && !serverMessage.includes('{') && !serverMessage.includes('Exception')) {
    return serverMessage;
  }

  return fallback;
}
